/**
 * Home-feed card: extreme-weather warning.
 *
 * Pulls a 24-hour forecast from open-meteo.com (free, no API key) for the
 * user's current GPS. If conditions are extreme — heatwave (>35°C),
 * cold-snap (<2°C), heavy rain (>15mm), strong wind (>50km/h), or snow —
 * a warning card is rendered. Otherwise the card stays hidden.
 *
 * Falls back silently when GPS isn't available.
 */

import { createLogger } from '../../core/logger.js';
import { escape as esc } from '../../shared/escape.js';

const log = createLogger('weather-card');

const CACHE_KEY = 'c2c_weather_cache';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1h
const GPS_CACHE_KEY = 'c2c_fuel_lastGps';   // share GPS cache with fuel card
const GPS_TTL_MS = 10 * 60 * 1000;

/** @returns {Promise<boolean>} true if card was rendered, false if hidden. */
export async function renderInto(host) {
  if (!host) return false;
  try {
    const gps = await getGps();
    if (!gps) return false;
    const warnings = await getWarnings(gps);
    if (warnings.length === 0) {
      host.style.display = 'none';
      return false;
    }
    host.style.display = '';
    host.classList.add('info-card', 'weather-warn');
    host.innerHTML = `
      <div class="info-card-header">⚠️ אזהרת מזג אוויר</div>
      <div class="weather-warn-body">
        ${warnings.map(esc).map((w) => `• ${w}`).join('<br>')}
      </div>
    `;
    return true;
  } catch (err) {
    log.warn('weather card failed:', err?.message);
    host.style.display = 'none';
    return false;
  }
}

// ====================================================================

async function getGps() {
  try {
    const raw = localStorage.getItem(GPS_CACHE_KEY);
    if (raw) {
      const c = JSON.parse(raw);
      if (c && c.at && Date.now() - c.at < GPS_TTL_MS && c.lat && c.lng) {
        return { lat: c.lat, lng: c.lng };
      }
    }
  } catch (_) { /* ignore */ }
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { maximumAge: GPS_TTL_MS, timeout: 6000 },
    );
  });
}

async function getWarnings({ lat, lng }) {
  // Cache to avoid hammering open-meteo on every render.
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const c = JSON.parse(raw);
      if (c && c.at && Date.now() - c.at < CACHE_TTL_MS && c.key === `${lat.toFixed(1)},${lng.toFixed(1)}`) {
        return c.warnings;
      }
    }
  } catch (_) { /* ignore */ }

  const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}`
    + '&hourly=temperature_2m,precipitation,wind_speed_10m,snowfall'
    + '&forecast_hours=24&timezone=Asia%2FJerusalem';
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('open-meteo HTTP ' + res.status);
  const j = await res.json();
  const h = j?.hourly ?? {};

  /** @type {string[]} */
  const warnings = [];
  const temps = /** @type {number[]} */ (h.temperature_2m || []);
  const precs = /** @type {number[]} */ (h.precipitation  || []);
  const winds = /** @type {number[]} */ (h.wind_speed_10m || []);
  const snows = /** @type {number[]} */ (h.snowfall       || []);

  const maxTemp  = Math.max(...temps);
  const minTemp  = Math.min(...temps);
  const totalPrec = precs.reduce((s, v) => s + (v || 0), 0);
  const maxWind  = Math.max(...winds);
  const totalSnow = snows.reduce((s, v) => s + (v || 0), 0);

  if (Number.isFinite(maxTemp) && maxTemp >= 38) warnings.push(`גל חום — צפויה טמפ' מקסימלית ${maxTemp.toFixed(0)}°C. דאג למים ברכב.`);
  else if (Number.isFinite(maxTemp) && maxTemp >= 35) warnings.push(`טמפ' גבוהה — צפויה עד ${maxTemp.toFixed(0)}°C. מנע השארת ילדים/חיות ברכב.`);
  if (Number.isFinite(minTemp) && minTemp <= 2) warnings.push(`קור עז — צפויה טמפ' מינ' ${minTemp.toFixed(0)}°C. שווה לבדוק לחץ אוויר וצמיגים.`);
  if (totalPrec >= 15) warnings.push(`גשם חזק צפוי (${totalPrec.toFixed(0)} מ"מ ב-24 שעות). הימנע מנהיגה בלתי הכרחית.`);
  if (Number.isFinite(maxWind) && maxWind >= 50) warnings.push(`רוחות חזקות (עד ${maxWind.toFixed(0)} קמ"ש). זהירות במעבר על גשרים ובאזורים פתוחים.`);
  if (totalSnow > 0) warnings.push(`צפוי שלג (${totalSnow.toFixed(1)} ס"מ). הימנע מנהיגה ללא ציוד חורף מתאים.`);

  try { localStorage.setItem(CACHE_KEY, JSON.stringify({
    at: Date.now(), key: `${lat.toFixed(1)},${lng.toFixed(1)}`, warnings,
  })); } catch (_) { /* ignore */ }

  return warnings;
}
